import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBoundStore } from "@/stores";
import SkeletonLoader from "@/shared/components/skeletons/SkeletonLoader";
import { MUST_COMPLETE_RESET_STATUSES } from "@/features/auth/types";

interface AuthGuardProps {
  children: React.ReactNode;
  /** Custom loading component (defaults to SkeletonLoader) */
  loadingFallback?: React.ReactNode;
}

/**
 * Auth Guard - redirects to login if not authenticated.
 * If authenticated but status is must-complete-reset (e.g. FIRST_LOGIN_PENDING),
 * redirects to /reset-password unless already there.
 */
const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  loadingFallback
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useBoundStore.use.isAuthenticated();
  const isAuthLoading = useBoundStore.use.isAuthLoading();
  const user = useBoundStore.use.user();
  const accountStatus = useBoundStore.use.accountStatus();

  const status = user?.status ?? accountStatus;
  const mustCompleteReset = status != null && MUST_COMPLETE_RESET_STATUSES.includes(status);
  const isOnResetPasswordPage = location.pathname === "/reset-password";

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    if (mustCompleteReset && !isOnResetPasswordPage) {
      navigate("/reset-password", { replace: true });
    }
  }, [isAuthenticated, isAuthLoading, mustCompleteReset, isOnResetPasswordPage, navigate]);

  if (isAuthLoading) {
    return <>{loadingFallback ?? <SkeletonLoader />}</>;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (mustCompleteReset && !isOnResetPasswordPage) {
    return null;
  }

  return <>{children}</>;
};

export default AuthGuard;